import { create } from 'zustand'
import { uid } from '../lib/cn'
import { diasHasta, estadoStock } from '../lib/format'
import { seguimientosPendientes } from '../lib/metricas'
import { enviarNotificacion } from '../lib/push'
import type {
  AccionBitacora,
  Ajustes,
  Asistencia,
  Bitacora,
  Cliente,
  ConfirmacionCliente,
  Devolucion,
  DiaDespacho,
  EstadoPedido,
  Gasto,
  LineaVenta,
  Lote,
  LoteVendido,
  Movimiento,
  Notificacion,
  Producto,
  ResultadoEntrega,
  Usuario,
  Venta,
} from '../types'
import type { Adapter, CambioStock, ResultadoStock, Snapshot, Tabla } from '../data/adapter'
import { FuncionPendienteError, snapshotVacio } from '../data/adapter'
import { siguienteDiaReparto } from '../lib/geo'
import { localAdapter } from '../data/localAdapter'
import { supabaseAdapter, supabaseConfigurado } from '../data/supabaseAdapter'
import { construirSemilla } from '../data/seed'

interface EstadoDatos extends Snapshot {
  listo: boolean
  cargando: boolean
  error: string | null
  adaptador: Adapter
  /** Nombre del backend activo, para mostrarlo en Ajustes. */
  backend: 'local' | 'supabase'

  inicializar: () => Promise<void>
  recargar: () => Promise<void>

  registrarVenta: (entrada: EntradaVenta, autor: Usuario) => Promise<Venta>
  cambiarEstadoVenta: (ventaId: string, estado: EstadoPedido, autor: Usuario) => Promise<void>
  marcarPagada: (ventaId: string, autor: Usuario) => Promise<void>
  marcarFacturada: (ventaId: string, numero: string, autor: Usuario) => Promise<void>
  anularVenta: (ventaId: string, autor: Usuario) => Promise<void>
  /** Cierra una entrega con lo que pasó de verdad (entregada, parcial,
   *  no estaba, rechazada), reponiendo al stock lo que volvió en el
   *  camión y registrando el cobro si se hizo en la puerta. */
  cerrarEntrega: (
    entrada: {
      ventaId: string
      resultado: ResultadoEntrega
      /** Solo para 'parcial': cuánto recibió de verdad, por producto. */
      recibido?: Record<string, number>
      /** Marca la venta como pagada en el momento de entregar. */
      cobro?: boolean
      nota?: string
    },
    autor: Usuario,
  ) => Promise<void>
  /** El despachador confirma que recibió y revisó el pedido. Si pasa
   *  algo (falta stock, dirección mala, etc.) manda una nota en
   *  `erroDespacho` en vez de dejarla vacía. */
  revisarDespacho: (ventaId: string, erroDespacho: string | undefined, autor: Usuario) => Promise<void>
  /** Resultado de llamar o escribir al cliente antes de salir. Si no
   *  confirma, el pedido se corre solo al próximo día de reparto. */
  confirmarCliente: (
    ventaId: string,
    resultado: ConfirmacionCliente,
    autor: Usuario,
  ) => Promise<void>
  /** Mueve un pedido a otro día de entrega, sin tocar nada más. */
  cambiarFechaDespacho: (
    ventaId: string,
    diaDespacho: DiaDespacho,
    fechaDespacho: string,
    autor: Usuario,
  ) => Promise<void>

  ingresarStock: (entrada: EntradaStock, autor: Usuario) => Promise<void>
  /** Registra la llegada de un pedido con varios productos de una vez
   *  —cada línea crea su propio lote— en lugar de cargarlos uno a uno. */
  registrarOrdenIngreso: (entrada: EntradaOrdenIngreso, autor: Usuario) => Promise<void>
  ajustarStock: (productoId: string, nuevoStock: number, motivo: string, autor: Usuario) => Promise<void>
  registrarDevolucion: (entrada: EntradaDevolucion, autor: Usuario) => Promise<void>

  guardarProducto: (producto: Producto, autor: Usuario) => Promise<void>
  eliminarProducto: (id: string, autor: Usuario) => Promise<void>
  guardarCliente: (cliente: Cliente, autor: Usuario) => Promise<void>
  eliminarCliente: (id: string, autor: Usuario) => Promise<void>
  guardarUsuario: (usuario: Usuario, autor: Usuario) => Promise<void>
  eliminarUsuario: (id: string, autor: Usuario) => Promise<void>
  guardarAjustes: (ajustes: Ajustes, autor: Usuario) => Promise<void>

  guardarGasto: (gasto: Gasto, autor: Usuario) => Promise<void>
  eliminarGasto: (id: string, autor: Usuario) => Promise<void>

  /** Abre una marca de entrada para esa persona en esa sucursal. No
   *  hace nada si ya tiene una abierta (sin salida) ahí mismo. */
  marcarEntrada: (sucursalId: string, trabajador: Usuario, autor: Usuario) => Promise<void>
  /** Cierra la marca de entrada más reciente sin salida de esa persona
   *  en esa sucursal. */
  marcarSalida: (sucursalId: string, usuarioId: string, autor: Usuario) => Promise<void>

  anotar: (autor: Usuario, accion: AccionBitacora, entidad: string, detalle: string, entidadId?: string) => Promise<void>
  exportarRespaldo: () => Snapshot
  restaurarRespaldo: (snapshot: Snapshot) => Promise<void>
  cargarDemo: () => Promise<void>
  borrarTodo: () => Promise<void>
}

export interface EntradaVenta {
  /** Id fijo para este carro, generado por la pantalla de venta al
   *  empezarlo. Si la señal se corta DESPUÉS de que la base guardó la
   *  venta pero ANTES de que llegue la respuesta, el vendedor ve un error
   *  y reintenta: con el mismo id, la base reconoce la venta que ya tiene
   *  y no la duplica ni vuelve a descontar stock. */
  id?: string
  clienteId: string
  lineas: { productoId: string; cantidad: number; precioUnitario: number }[]
  metodoPago: Venta['metodoPago']
  estado: EstadoPedido
  observaciones?: string
  vencimientoPago?: string
  diaDespacho?: Venta['diaDespacho']
  fechaDespacho?: string
  direccionDespacho?: string
  entregaSinAviso?: boolean
}

export interface EntradaStock {
  productoId: string
  cantidad: number
  proveedor: string
  fecha: string
  costo: number
  vencimiento?: string
  codigoLote?: string
  recibidoPor?: string
  observaciones?: string
}

/** Una orden de ingreso: llega un camión con varios productos a la
 *  vez, cada uno con su propio lote, costo y vencimiento, pero
 *  comparten fecha, proveedor y quién recibió. */
export interface EntradaOrdenIngreso {
  fecha: string
  proveedor: string
  recibidoPor: string
  observaciones?: string
  lineas: {
    productoId: string
    cantidad: number
    costo: number
    vencimiento?: string
    codigoLote?: string
  }[]
}

export interface EntradaDevolucion {
  ventaId: string
  productoId: string
  cantidad: number
  motivo: string
  reponeStock: boolean
}

const ahora = () => new Date().toISOString()

/** "preparando" y "despachada" son los dos estados que hacen que una
 *  venta aparezca en la cola de Despachos (ver `pendientes` en
 *  `pages/Despachos.tsx`) —o sea, los dos que de verdad significan
 *  "hay que ir a repartir esto". */
const ESTADOS_DE_DESPACHO = new Set<EstadoPedido>(['preparando', 'despachada'])

/** Avisa por push al despachador que hay un pedido nuevo esperando. No
 *  hace nada si la venta no entró recién a la cola (evita avisar en
 *  cada edición menor) ni si no hay cuenta de Despachos creada. Nunca
 *  lanza: un aviso que no llegó no debe tumbar la venta. */
function avisarDespachadorSiCorresponde(
  usuarios: Usuario[],
  venta: Venta,
  estadoAnterior?: EstadoPedido,
) {
  if (!ESTADOS_DE_DESPACHO.has(venta.estado)) return
  if (estadoAnterior && ESTADOS_DE_DESPACHO.has(estadoAnterior)) return
  const despachador = usuarios.find((u) => u.rol === 'despacho')
  if (!despachador) return
  void enviarNotificacion(
    despachador.id,
    'Nuevo despacho',
    `${venta.folio} · ${venta.clienteNombre}`,
    '/despachos',
  )
}

/** Deja las colecciones cronológicas de la más reciente a la más
 *  antigua.
 *
 *  Hace falta porque ningún backend garantiza el orden: IndexedDB
 *  devuelve por clave primaria y Supabase sin ORDER BY no promete nada.
 *  Toda la app asume "lo último primero" —últimas ventas, historial de
 *  movimientos, bitácora—, así que se normaliza una vez al cargar en vez
 *  de repetir el sort en cada pantalla. */
function ordenar(s: Snapshot): Snapshot {
  const recientePrimero = (a: { fecha: string }, b: { fecha: string }) =>
    b.fecha.localeCompare(a.fecha)
  return {
    ...s,
    ventas: [...s.ventas].sort(recientePrimero),
    movimientos: [...s.movimientos].sort(recientePrimero),
    bitacora: [...s.bitacora].sort(recientePrimero),
    devoluciones: [...s.devoluciones].sort(recientePrimero),
    gastos: [...s.gastos].sort(recientePrimero),
    lotes: [...s.lotes].sort((a, b) => b.ingresado.localeCompare(a.ingresado)),
  }
}

/** Cuando Supabase está configurado se usa; si no, la app corre igual
 *  contra IndexedDB. Nunca queda inutilizable por falta de credenciales. */
const elegirAdaptador = (): Adapter => (supabaseConfigurado ? supabaseAdapter : localAdapter)

/** Deja en memoria el stock y los movimientos tal como quedaron en la
 *  base.
 *
 *  La app descuenta el stock en pantalla al tiro para que vender sea
 *  instantáneo, pero ese número es una apuesta: el POS del local vende
 *  sobre los mismos productos y la copia en memoria puede venir de hace
 *  horas. El backend devuelve el stock de verdad y acá se pisa lo que
 *  había — así la pantalla se corrige sola, sin necesidad de recargar. */
const conStockDeLaBase =
  (resultado: ResultadoStock) =>
  (s: EstadoDatos): Partial<EstadoDatos> => ({
    productos: s.productos.map((p) =>
      p.id in resultado.stock ? { ...p, stock: resultado.stock[p.id] } : p,
    ),
    movimientos: s.movimientos.map((m) => {
      const real = resultado.movimientos.find((x) => x.id === m.id)
      return real ? { ...m, ...real } : m
    }),
  })

/** Aplica un cambio de stock y, si lo que viene DESPUÉS falla, lo
 *  devuelve.
 *
 *  Varias operaciones mueven stock y además guardan otra cosa (la venta,
 *  la anulación, la entrega). Son escrituras separadas contra la base,
 *  así que si la segunda falla la primera ya quedó hecha: el stock se
 *  movió por algo que no existe. Peor: al reintentar, se vuelve a mover.
 *  El 17-09-2026 eso descontó 60 frambuesas en cinco intentos de una
 *  misma venta que nunca llegó a guardarse.
 *
 *  La reversa queda como un movimiento más ("ajuste"), con el mismo
 *  `referenciaId` y una observación que lo explica: en Movimientos se ve
 *  el intento y su devolución, en vez de un stock que cambió sin razón.
 *
 *  SOLO para movimientos con id al azar. La entrega y la anulación usan
 *  ids fijos (`m_entrega_…`, `m_anul_…`) para que reintentarlas sea
 *  seguro por otra vía: la base reconoce el movimiento ya aplicado y no
 *  lo repite. Si además se devolviera el stock al fallar, el reintento
 *  vería el movimiento como "ya hecho", lo saltaría, y el stock no
 *  volvería nunca. Hoy lo usa únicamente el camino de respaldo de
 *  registrarVenta. */
async function conStockReversible<T>(
  adaptador: Adapter,
  movimientos: CambioStock[],
  despues: () => Promise<T>,
  motivo: string,
): Promise<ResultadoStock> {
  const resultado = await adaptador.aplicarStock(movimientos)
  try {
    await despues()
  } catch (e) {
    const reversos: CambioStock[] = resultado.movimientos
      .filter((m) => m.cantidad !== 0)
      .map((m) => {
        const original = movimientos.find((o) => o.id === m.id)
        return {
          id: uid('m'),
          productoId: m.productoId,
          tipo: 'ajuste',
          cantidad: -m.cantidad,
          referenciaId: original?.referenciaId,
          usuarioId: original?.usuarioId ?? 'sistema',
          usuarioNombre: original?.usuarioNombre ?? 'Sistema',
          observaciones: `Reversa automática: ${motivo} no alcanzó a guardarse.`,
          fecha: ahora(),
        }
      })
    try {
      if (reversos.length) await adaptador.aplicarStock(reversos)
    } catch {
      // Ni la devolución alcanzó a llegar (casi siempre: se cortó la
      // señal justo ahí). No se esconde: el error dice qué revisar.
      const detalle = e instanceof Error ? e.message : String(e)
      throw new Error(
        `${detalle} — y el stock ya descontado no se pudo devolver: revisa Movimientos (referencia ${movimientos[0]?.referenciaId ?? 'sin referencia'}).`,
      )
    }
    throw e
  }
  return resultado
}

/** Toma nota de cómo están ciertas listas del store, para poder
 *  devolverlas a ese punto exacto si la escritura al backend falla.
 *
 *  Toda la app cambia la pantalla al tiro y escribe después, para que
 *  usar el teléfono en terreno sea instantáneo aunque la señal esté
 *  mala. El problema era qué pasaba cuando esa escritura fallaba: sin
 *  esto, la pantalla se quedaba mostrando un cambio que nunca llegó a
 *  la base —un cliente que no existe, un precio que nadie guardó— y
 *  recién al recargar la app volvía sola atrás, sin decir una palabra.
 *  El usuario creía haber guardado y no había guardado nada.
 *
 *  Es barato: todas las mutaciones de este store copian en vez de
 *  editar en el lugar, así que "guardar cómo estaba" es guardar una
 *  referencia a la lista anterior, no clonarla.
 *
 *  Deshacer devuelve la lista COMPLETA a como estaba, así que también
 *  descarta cualquier otro cambio que hubiera entrado en esa misma
 *  lista mientras tanto. Acá eso es seguro porque la app hace una cosa
 *  a la vez: cada pantalla bloquea su botón mientras la operación está
 *  en curso. */
function respaldar(get: () => EstadoDatos, ...listas: (Tabla | 'ajustes')[]) {
  const previo: Partial<EstadoDatos> = {}
  for (const lista of listas) {
    ;(previo as Record<string, unknown>)[lista] = get()[lista]
  }
  return previo
}

export const useDatos = create<EstadoDatos>((set, get) => ({
  ...snapshotVacio(),
  listo: false,
  cargando: false,
  error: null,
  adaptador: elegirAdaptador(),
  backend: supabaseConfigurado ? 'supabase' : 'local',

  async inicializar() {
    if (get().cargando) return
    set({ cargando: true, error: null })
    let adaptador = get().adaptador
    try {
      if (adaptador.nombre === 'supabase' && !(await adaptador.disponible())) {
        // Credenciales presentes pero la base no responde: se sigue en
        // local para no dejar al vendedor sin herramienta.
        adaptador = localAdapter
        set({
          adaptador,
          backend: 'local',
          error: 'No se pudo conectar con Supabase. Trabajando con los datos de este dispositivo.',
        })
      }
      let snapshot = await adaptador.cargar()
      // Sembrar datos de demostración solo tiene sentido en el dispositivo
      // local, donde "vacío" significa de verdad una instalación nueva.
      // En Supabase, una tabla "usuarios" vacía casi siempre significa que
      // aún falta insertar la primera cuenta (ver supabase/schema.sql) —
      // nunca hay que rellenar una base de datos compartida con productos
      // y ventas falsas sin que el administrador lo pida explícitamente.
      if (!snapshot.usuarios.length && adaptador.nombre === 'local') {
        snapshot = construirSemilla()
        await adaptador.reemplazarTodo(snapshot)
      }
      set({ ...ordenar(snapshot), listo: true, cargando: false })
    } catch (e) {
      set({ cargando: false, error: e instanceof Error ? e.message : 'Error al cargar los datos.' })
    }
  },

  async recargar() {
    const snapshot = await get().adaptador.cargar()
    set({ ...ordenar(snapshot) })
  },

  /* --------------------------- Ventas --------------------------- */

  async registrarVenta(entrada, autor) {
    const { productos, lotes, ajustes, clientes, ventas, adaptador } = get()
    const cliente = clientes.find((c) => c.id === entrada.clienteId)
    if (!cliente) throw new Error('Selecciona un cliente para continuar.')
    if (!entrada.lineas.length) throw new Error('Agrega al menos un producto.')

    const productosTocados: Producto[] = []
    const lotesTocados: Lote[] = []
    const movimientos: Movimiento[] = []
    const lineas: LineaVenta[] = []
    const fecha = ahora()
    const ventaId = entrada.id ?? uid('v')

    for (const l of entrada.lineas) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) throw new Error('Uno de los productos ya no existe.')
      const disponible = p.stock - p.reservado
      if (l.cantidad > disponible) {
        throw new Error(`${p.nombre}: solo quedan ${disponible} disponibles.`)
      }

      const actualizado = productosTocados.find((x) => x.id === p.id) ?? { ...p }
      actualizado.stock -= l.cantidad
      if (!productosTocados.some((x) => x.id === p.id)) productosTocados.push(actualizado)

      // FEFO: se descuenta primero del lote que vence antes, para que lo
      // más antiguo salga primero de la cámara.
      let restante = l.cantidad
      let costoAcumulado = 0
      const suyos = lotes
        .filter((lo) => lo.productoId === p.id && lo.cantidad > 0)
        .sort((a, b) => (a.vencimiento ?? '9999').localeCompare(b.vencimiento ?? '9999'))
      let loteUsado: string | undefined
      // TODOS los lotes de los que sale esta línea, no solo el primero:
      // es lo único que permite responder "a qué clientes les llegó el
      // lote X" si hay que retirar un producto.
      const lotesDeLaLinea: LoteVendido[] = []
      for (const lo of suyos) {
        if (restante <= 0) break
        const toma = Math.min(lo.cantidad, restante)
        const copia = lotesTocados.find((x) => x.id === lo.id) ?? { ...lo }
        copia.cantidad -= toma
        if (!lotesTocados.some((x) => x.id === lo.id)) lotesTocados.push(copia)
        // El costo real de la línea sale del lote que de verdad se
        // consumió, no del costo genérico del producto —si la fruta
        // llegó más cara en el lote nuevo, la utilidad de esta venta
        // tiene que reflejarlo, no la de la próxima.
        costoAcumulado += toma * lo.costo
        lotesDeLaLinea.push({ loteId: lo.id, codigo: lo.codigo, cantidad: toma, costo: lo.costo })
        restante -= toma
        loteUsado ??= lo.id
      }
      // Si el descuadre entre `stock` y `lotes` (ver anularVenta) deja
      // sin lotes de dónde sacar el resto, ese remanente se completa al
      // costo actual del producto —mejor una utilidad aproximada que
      // una venta que no se puede registrar.
      if (restante > 0) costoAcumulado += restante * p.precioCosto
      const costoUnitario = l.cantidad > 0 ? costoAcumulado / l.cantidad : p.precioCosto

      lineas.push({
        productoId: p.id,
        nombre: p.nombre,
        codigo: p.codigo,
        cantidad: l.cantidad,
        precioUnitario: l.precioUnitario,
        costoUnitario,
        subtotal: l.precioUnitario * l.cantidad,
        loteId: loteUsado,
        lotes: lotesDeLaLinea.length ? lotesDeLaLinea : undefined,
      })

      movimientos.push({
        id: uid('m'),
        productoId: p.id,
        tipo: 'venta',
        cantidad: -l.cantidad,
        stockResultante: actualizado.stock,
        loteId: loteUsado,
        referenciaId: ventaId,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        fecha,
      })
    }

    const total = lineas.reduce((s, l) => s + l.subtotal, 0)
    const costoTotal = lineas.reduce((s, l) => s + l.costoUnitario * l.cantidad, 0)
    const ultimoFolio = ventas.reduce((max, v) => {
      const n = Number(v.folio.replace(/\D/g, ''))
      return Number.isFinite(n) && n > max ? n : max
    }, 1000)

    const venta: Venta = {
      id: ventaId,
      folio: `V-${ultimoFolio + 1}`,
      clienteId: cliente.id,
      clienteNombre: [cliente.nombre, cliente.apellido].filter(Boolean).join(' '),
      clienteTipo: cliente.tipo,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      lineas,
      total,
      costoTotal,
      utilidad: total - costoTotal,
      // Se calcula sobre la utilidad (venta menos costo), no sobre el
      // total vendido —la comisión del 30% es sobre ganancia real, no
      // sobre ingresos. Se usa la tasa del vendedor en este momento, no
      // la que tenga después: así un cambio de tasa no reescribe
      // comisiones ya ganadas.
      comision: Math.round((total - costoTotal) * ((autor.comision || 0) / 100)),
      metodoPago: entrada.metodoPago,
      estado: entrada.estado,
      // Una venta solo nace pagada si se entrega EN EL MOMENTO. Antes
      // bastaba con que no fuera a crédito, así que un pedido en
      // efectivo tomado el jueves para repartir el martes quedaba
      // marcado como pagado desde el jueves —cuatro días antes de que
      // existiera la plata—, y como Cobranza solo mira las ventas a
      // crédito, ese dinero no aparecía en ninguna lista de por cobrar.
      // Nadie podía saber cuánto efectivo debía traer de vuelta el
      // despachador.
      pagado: entrada.metodoPago !== 'credito' && entrada.estado === 'entregada',
      vencimientoPago:
        entrada.metodoPago === 'credito'
          ? (entrada.vencimientoPago ??
            new Date(Date.now() + ajustes.diasCredito * 86_400_000).toISOString().slice(0, 10))
          : undefined,
      fechaPago: entrada.metodoPago !== 'credito' && entrada.estado === 'entregada' ? fecha : undefined,
      cobradoPorId: entrada.metodoPago !== 'credito' && entrada.estado === 'entregada' ? autor.id : undefined,
      cobradoPorNombre:
        entrada.metodoPago !== 'credito' && entrada.estado === 'entregada' ? autor.nombre : undefined,
      // Si la venta ya nace "entregada" (el caso más común: se entrega
      // en el momento), la fecha de entrega es la misma de la venta.
      fechaEntrega: entrada.estado === 'entregada' ? fecha : undefined,
      resultadoEntrega: entrada.estado === 'entregada' ? 'entregado' : undefined,
      diaDespacho: entrada.diaDespacho,
      fechaDespacho: entrada.fechaDespacho,
      direccionDespacho: entrada.direccionDespacho,
      entregaSinAviso: entrada.entregaSinAviso,
      observaciones: entrada.observaciones,
      fecha,
    }

    // Copia de cómo estaban los productos y lotes tocados ANTES de
    // descontarles el stock: si el guardado falla hay que devolver todo
    // a este punto.
    const productosPrevios = productosTocados
      .map((t) => productos.find((p) => p.id === t.id))
      .filter((p): p is Producto => !!p)
    const lotesPrevios = lotesTocados
      .map((t) => lotes.find((l) => l.id === t.id))
      .filter((l): l is Lote => !!l)

    set((s) => ({
      ventas: [venta, ...s.ventas],
      productos: s.productos.map((p) => productosTocados.find((x) => x.id === p.id) ?? p),
      lotes: s.lotes.map((l) => lotesTocados.find((x) => x.id === l.id) ?? l),
      movimientos: [...movimientos, ...s.movimientos],
    }))

    // La pantalla se actualiza al tiro (arriba) para que vender sea
    // instantáneo aunque la señal esté mala, pero si la escritura falla
    // hay que deshacer eso: si no, queda una venta fantasma —visible en
    // la app, con el stock ya descontado— que en la base de datos no
    // existe y que no aparece en Despachos. Eso es peor que un error:
    // el vendedor cree que vendió y nadie va a despachar ese pedido.
    try {
      // Stock, venta y lotes en UNA sola transacción (nb_registrar_venta
      // en Supabase, una transacción de IndexedDB en local): o queda
      // todo o no queda nada. La base resta sobre el stock que tiene
      // guardado, así que si el POS del local ya vendió esas unidades,
      // la venta se rechaza entera con el nombre del producto y el stock
      // real, y el vendedor puede reintentar sin haber ensuciado nada.
      let resultado: ResultadoStock & { folio?: string; repetida?: boolean }
      try {
        resultado = await adaptador.registrarVenta(venta, movimientos, lotesTocados)
      } catch (e) {
        if (!(e instanceof FuncionPendienteError)) throw e
        // La base todavía no tiene nb_registrar_venta. Se hace en pasos,
        // pero con red: si la venta no alcanza a guardarse, el stock
        // descontado se devuelve solo en vez de quedar perdido.
        resultado = await conStockReversible(
          adaptador,
          movimientos,
          () =>
            Promise.all([
              adaptador.guardar('ventas', [venta]),
              adaptador.guardar('lotes', lotesTocados),
            ]),
          `la venta ${venta.folio}`,
        )
      }
      // Otro teléfono alcanzó a usar este mismo folio primero: la base
      // le asignó el siguiente libre y acá se muestra el que quedó.
      if (resultado.folio && resultado.folio !== venta.folio) {
        venta.folio = resultado.folio
        set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? { ...v, folio: venta.folio } : v)) }))
      }
      set(conStockDeLaBase(resultado))
      // Un reintento de una venta que en realidad sí se había guardado
      // (la respuesta se perdió en el camino): la base no hizo nada nuevo,
      // así que lo que hay en memoria se trae de nuevo tal como está allá.
      if (resultado.repetida) void get().recargar()
    } catch (err) {
      const idsMovimientos = new Set(movimientos.map((m) => m.id))
      set((s) => ({
        ventas: s.ventas.filter((v) => v.id !== ventaId),
        productos: s.productos.map((p) => productosPrevios.find((x) => x.id === p.id) ?? p),
        lotes: s.lotes.map((l) => lotesPrevios.find((x) => x.id === l.id) ?? l),
        movimientos: s.movimientos.filter((m) => !idsMovimientos.has(m.id)),
      }))
      const detalle = err instanceof Error ? err.message : String(err)
      throw new Error(`La venta NO quedó registrada: ${detalle}`)
    }

    await get().anotar(autor, 'venta', 'venta', `${venta.folio} · ${venta.clienteNombre}`, venta.id)
    // Si nació directo en la cola de despacho (el vendedor la marcó
    // "En preparación" al vender), el despachador se entera al tiro,
    // no cuando alguien más tarde revise Pedidos.
    avisarDespachadorSiCorresponde(get().usuarios, venta)
    return venta
  },

  async cambiarEstadoVenta(ventaId, estado, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return

    // Al entregar un despacho fuera de ruta se congela lo que se le
    // paga al despachador por ese viaje. Se calcula UNA vez, acá, y no
    // se vuelve a tocar: si mañana cambia el tarifario, este viaje se
    // sigue pagando a lo que valía el día que se hizo. Es el mismo
    // criterio que la comisión del vendedor.
    let pago: Pick<Venta, 'tarifaDespacho' | 'kmDespacho'> = {}
    const recienEntregada = estado === 'entregada' && venta.estado !== 'entregada'
    if (recienEntregada && venta.diaDespacho === 'especial' && venta.tarifaDespacho === undefined) {
      const { ajustes, clientes } = get()
      const cliente = clientes.find((c) => c.id === venta.clienteId)
      if (cliente?.lat && cliente.lng) {
        const { distanciaKm, tarifaDespacho } = await import('../lib/geo')
        const km = distanciaKm(
          { lat: ajustes.bodegaLat, lng: ajustes.bodegaLng },
          { lat: cliente.lat, lng: cliente.lng },
        )
        pago = {
          kmDespacho: km,
          tarifaDespacho: tarifaDespacho(
            km,
            ajustes.tarifaDespachoBase,
            ajustes.tarifaDespachoPorKm,
          ),
        }
      }
    }

    const actualizada: Venta = {
      ...venta,
      estado,
      ...pago,
      // Se registra la primera vez que llega a "entregada"; si ya
      // estaba marcada y alguien la mueve hacia atrás y adelante, la
      // fecha real de la primera entrega no se pierde.
      fechaEntrega: estado === 'entregada' ? (venta.fechaEntrega ?? ahora()) : venta.fechaEntrega,
    }
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))

    let guardada = actualizada
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      // Si las columnas de la tarifa todavía no existen en la base
      // —falta correr supabase/despachador.sql—, Supabase rechaza la
      // fila ENTERA y la entrega no se registraría. Marcar entregado es
      // mucho más importante que anotar cuánto se paga por ese viaje:
      // se reintenta sin esos dos campos y el pago queda como estimado,
      // que es exactamente lo que hacía la app antes de guardarlos.
      const falta = /tarifaDespacho|kmDespacho/i.test(
        e instanceof Error ? e.message : String(e),
      )
      if (!falta || pago.tarifaDespacho === undefined) throw e

      const sinTarifa = { ...actualizada }
      delete sinTarifa.tarifaDespacho
      delete sinTarifa.kmDespacho
      await get().adaptador.guardar('ventas', [sinTarifa])
      guardada = sinTarifa
      // La copia en memoria tiene que quedar igual que la base, o el
      // despachador vería un monto congelado que no está guardado.
      set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? sinTarifa : v)) }))
    }

    await get().anotar(autor, 'editar', 'venta', `${venta.folio} pasó a ${estado}`, ventaId)
    // Si recién ahora entra a la cola de despacho (por ejemplo, desde
    // Pedidos alguien la marca "Despachar"), el aviso es nuevo. Si ya
    // estaba en la cola y solo cambia de un estado de despacho a otro,
    // no se repite el aviso.
    avisarDespachadorSiCorresponde(get().usuarios, guardada, venta.estado)
  },

  async revisarDespacho(ventaId, erroDespacho, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    // OJO: mandar '' y no undefined para "sin problema" —Supabase hace
    // upsert con JSON, y JSON.stringify borra las claves en undefined
    // del payload, así que la columna se quedaría con el valor viejo.
    let actualizada: Venta = { ...venta, revisadoDespacho: true, erroDespacho: erroDespacho || '' }

    // Un pedido con problema (falta stock, dirección mala) NO sale hoy:
    // se corre al próximo reparto. Antes solo se anotaba el problema y
    // el pedido se quedaba en la lista de "por confirmar" para siempre
    // —y como la ruta del día no se arma hasta que esa lista esté
    // vacía, reportar un problema dejaba al despachador sin poder salir
    // a repartir nada.
    if (erroDespacho) {
      const siguiente = siguienteDiaReparto(venta.fechaDespacho)
      actualizada = {
        ...actualizada,
        diaDespacho: siguiente.dia,
        fechaDespacho: siguiente.fecha,
        vecesPospuesto: (venta.vecesPospuesto ?? 0) + 1,
        // Al cambiar de día vuelve a empezar: hay que revisarlo y
        // confirmarlo de nuevo cuando llegue esa fecha.
        revisadoDespacho: false,
        confirmacionCliente: null,
      }
    }

    const previo = respaldar(get, 'ventas')
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'editar',
      'venta',
      erroDespacho
        ? `${venta.folio} con problema (${erroDespacho}): se corrió al próximo reparto`
        : `${venta.folio} recibido y revisado por el despachador`,
      ventaId,
    )
  },

  async confirmarCliente(ventaId, resultado, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return

    let actualizada: Venta = { ...venta, confirmacionCliente: resultado }
    let detalle = `${venta.folio}: el cliente confirmó la entrega`

    // Si no hubo confirmación, el pedido no sale hoy: se corre solo al
    // próximo día de reparto para que no quede colgado ni haya que
    // acordarse a mano.
    if (resultado !== 'confirmado') {
      const siguiente = siguienteDiaReparto(venta.fechaDespacho)
      actualizada = {
        ...actualizada,
        diaDespacho: siguiente.dia,
        fechaDespacho: siguiente.fecha,
        vecesPospuesto: (venta.vecesPospuesto ?? 0) + 1,
        // Al cambiar de día vuelve a empezar: hay que revisarlo y
        // confirmarlo de nuevo cuando llegue esa fecha.
        revisadoDespacho: false,
        confirmacionCliente: null,
      }
      const motivo = resultado === 'no_contesto' ? 'no contestó' : 'no confirmó'
      detalle = `${venta.folio}: ${motivo}, se corre al ${siguiente.dia} ${siguiente.fecha}`
    }

    const previo = respaldar(get, 'ventas')
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'editar', 'venta', detalle, ventaId)
  },

  async cambiarFechaDespacho(ventaId, diaDespacho, fechaDespacho, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    const actualizada: Venta = {
      ...venta,
      diaDespacho,
      fechaDespacho,
      // Cambió el día: lo que ya se había revisado o confirmado para la
      // fecha anterior deja de valer.
      revisadoDespacho: false,
      confirmacionCliente: null,
      // Y si era una entrega sin aviso, deja de serlo: al cliente le
      // dijeron otra fecha, así que ahora sí hay que llamarlo. Va en
      // `false` y no `undefined` porque undefined se pierde al guardar
      // y la columna quedaría en true.
      entregaSinAviso: false,
    }
    const previo = respaldar(get, 'ventas')
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'editar',
      'venta',
      `${venta.folio}: entrega movida al ${fechaDespacho}`,
      ventaId,
    )
  },

  async marcarPagada(ventaId, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    // Se guarda quién y cuándo cobró: es lo que permite cuadrar al
    // final del día el efectivo que trae de vuelta el despachador.
    const actualizada = {
      ...venta,
      pagado: true,
      fechaPago: venta.fechaPago ?? ahora(),
      cobradoPorId: venta.cobradoPorId ?? autor.id,
      cobradoPorNombre: venta.cobradoPorNombre ?? autor.nombre,
    }
    const previo = respaldar(get, 'ventas')
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    // Marcar pagada es plata: si la escritura falla y la pantalla igual
    // muestra la venta cobrada, esa deuda desaparece de Cobranza hasta
    // que alguien recargue, y nadie la va a ir a cobrar.
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'editar', 'cobranza', `${venta.folio} quedó pagada`, ventaId)
  },

  /** Cierra una entrega con lo que pasó de verdad: entregada completa,
   *  parcial, el cliente no estaba o rechazó el pedido. Antes lo único
   *  registrable era "entregado", así que los otros tres casos no
   *  tenían dónde anotarse y el pedido quedaba dando vueltas sin
   *  explicación.
   *
   *  - `parcial` repone al stock lo que el cliente no recibió y ajusta
   *    el total de la venta a lo realmente entregado.
   *  - `ausente` y `rechazado` devuelven TODO al stock: la mercadería
   *    volvió en el camión. `ausente` además corre el pedido al
   *    próximo día de reparto en vez de darlo por perdido.
   *  - `cobro` registra la plata recibida en la entrega, para las
   *    ventas que no son a crédito. */
  async cerrarEntrega(entrada, autor) {
    const { ventas, productos, lotes, adaptador } = get()
    const venta = ventas.find((v) => v.id === entrada.ventaId)
    if (!venta) throw new Error('No se encontró la venta.')

    const devueltoPorLinea = new Map<string, number>()
    let lineasFinales = venta.lineas

    if (entrada.resultado === 'parcial') {
      lineasFinales = venta.lineas.map((l) => {
        const recibido = entrada.recibido?.[l.productoId]
        const entregada = recibido === undefined ? l.cantidad : Math.max(0, Math.min(recibido, l.cantidad))
        if (entregada < l.cantidad) devueltoPorLinea.set(l.productoId, l.cantidad - entregada)
        return { ...l, cantidadEntregada: entregada }
      })
    } else if (entrada.resultado === 'ausente' || entrada.resultado === 'rechazado') {
      // No recibió nada: todo lo que iba en el camión vuelve.
      lineasFinales = venta.lineas.map((l) => ({ ...l, cantidadEntregada: 0 }))
      for (const l of venta.lineas) devueltoPorLinea.set(l.productoId, l.cantidad)
    }

    // Reponer al stock (y a su lote de origen) lo que no se entregó.
    const tocados: Producto[] = []
    const lotesTocados: Lote[] = []
    const movimientos: Movimiento[] = []
    for (const [productoId, cantidad] of devueltoPorLinea) {
      if (cantidad <= 0) continue
      const p = productos.find((x) => x.id === productoId)
      if (!p) continue
      const copia = tocados.find((x) => x.id === p.id) ?? { ...p }
      copia.stock += cantidad
      if (!tocados.some((x) => x.id === p.id)) tocados.push(copia)

      // Se devuelve a los mismos lotes de los que salió, en orden.
      const linea = venta.lineas.find((l) => l.productoId === productoId)
      const origen: LoteVendido[] =
        linea?.lotes ?? (linea?.loteId ? [{ loteId: linea.loteId, cantidad, costo: linea.costoUnitario }] : [])
      let porDevolver = cantidad
      for (const lv of origen) {
        if (porDevolver <= 0) break
        const lote = lotesTocados.find((x) => x.id === lv.loteId) ?? lotes.find((x) => x.id === lv.loteId)
        if (!lote) continue
        const devuelve = Math.min(porDevolver, lv.cantidad)
        const copiaLote = { ...lote, cantidad: Math.min(lote.cantidad + devuelve, lote.cantidadInicial) }
        const i = lotesTocados.findIndex((x) => x.id === lote.id)
        if (i >= 0) lotesTocados[i] = copiaLote
        else lotesTocados.push(copiaLote)
        porDevolver -= devuelve
      }

      movimientos.push({
        // Id fijo, no al azar, por lo mismo que en anularVenta: si el
        // despachador cierra la entrega con mala señal y tiene que
        // reintentar, la mercadería no vuelve al stock dos veces.
        id: `m_entrega_${venta.id}_${p.id}`,
        productoId: p.id,
        tipo: 'ajuste',
        cantidad,
        stockResultante: copia.stock,
        referenciaId: venta.id,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        observaciones: `${venta.folio}: volvió del reparto (${entrada.resultado})`,
        fecha: ahora(),
      })
    }

    const totalFinal = lineasFinales.reduce(
      (s, l) => s + l.precioUnitario * (l.cantidadEntregada ?? l.cantidad),
      0,
    )
    const costoFinal = lineasFinales.reduce(
      (s, l) => s + l.costoUnitario * (l.cantidadEntregada ?? l.cantidad),
      0,
    )

    const entregoAlgo = entrada.resultado === 'entregado' || entrada.resultado === 'parcial'
    const cobra = Boolean(entrada.cobro) && entregoAlgo
    const ahoraISO = ahora()

    // Si no estaba, se corre al próximo día de reparto en vez de darlo
    // por perdido —es el mismo criterio que ya se usa cuando el cliente
    // no confirma antes de salir.
    const reprogramar = entrada.resultado === 'ausente'
    const siguiente = reprogramar ? siguienteDiaReparto(venta.fechaDespacho) : null

    const actualizada: Venta = {
      ...venta,
      lineas: lineasFinales,
      total: entregoAlgo ? totalFinal : 0,
      costoTotal: entregoAlgo ? costoFinal : 0,
      utilidad: entregoAlgo ? totalFinal - costoFinal : 0,
      comision: entregoAlgo
        ? Math.round((totalFinal - costoFinal) * ((autor.comision || 0) / 100))
        : 0,
      resultadoEntrega: entrada.resultado,
      notaEntrega: entrada.nota?.trim() || venta.notaEntrega,
      estado: reprogramar ? 'preparando' : entregoAlgo ? 'entregada' : 'anulada',
      fechaEntrega: entregoAlgo ? (venta.fechaEntrega ?? ahoraISO) : undefined,
      ...(reprogramar && siguiente
        ? {
            diaDespacho: siguiente.dia,
            fechaDespacho: siguiente.fecha,
            confirmacionCliente: null,
            vecesPospuesto: (venta.vecesPospuesto ?? 0) + 1,
          }
        : {}),
      ...(cobra
        ? {
            pagado: true,
            fechaPago: ahoraISO,
            cobradoPorId: autor.id,
            cobradoPorNombre: autor.nombre,
          }
        : {}),
    }

    const previo = respaldar(get, 'ventas', 'productos', 'lotes', 'movimientos')
    set((s) => ({
      ventas: s.ventas.map((v) => (v.id === venta.id ? actualizada : v)),
      productos: s.productos.map((p) => tocados.find((x) => x.id === p.id) ?? p),
      lotes: s.lotes.map((l) => lotesTocados.find((x) => x.id === l.id) ?? l),
      movimientos: [...movimientos, ...s.movimientos],
    }))

    // Mismo arreglo que en anularVenta: iban las tres juntas y una
    // podía quedar sin hacerse. Acá era peor todavía, porque esta
    // función además marca la venta como cobrada: podía quedar la plata
    // registrada como recibida y la mercadería devuelta al stock, o al
    // revés, según cuál de las escrituras alcanzara a pasar.
    try {
      const resultado = movimientos.length ? await adaptador.aplicarStock(movimientos) : null
      await Promise.all([
        adaptador.guardar('ventas', [actualizada]),
        lotesTocados.length ? adaptador.guardar('lotes', lotesTocados) : Promise.resolve(),
      ])
      if (resultado) set(conStockDeLaBase(resultado))
    } catch (e) {
      set(previo)
      throw e
    }

    const etiqueta =
      entrada.resultado === 'entregado'
        ? 'entregada'
        : entrada.resultado === 'parcial'
          ? 'entregada en parte'
          : entrada.resultado === 'ausente'
            ? 'no estaba: se corrió de día'
            : 'rechazada por el cliente'
    await get().anotar(autor, 'editar', 'venta', `${venta.folio}: ${etiqueta}`, venta.id)
  },

  async marcarFacturada(ventaId, numero, autor) {
    const venta = get().ventas.find((v) => v.id === ventaId)
    if (!venta) return
    const actualizada = {
      ...venta,
      facturaEmitida: true,
      facturaNumero: numero.trim() || '',
      facturaFecha: ahora(),
      facturaPorNombre: autor.nombre,
    }
    const previo = respaldar(get, 'ventas')
    set((s) => ({ ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)) }))
    try {
      await get().adaptador.guardar('ventas', [actualizada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'editar',
      'cobranza',
      `Factura ${numero ? `N° ${numero} ` : ''}emitida para ${venta.folio}`,
      ventaId,
    )
  },

  async anularVenta(ventaId, autor) {
    const { ventas, productos, lotes, adaptador } = get()
    const venta = ventas.find((v) => v.id === ventaId)
    if (!venta || venta.estado === 'anulada') return

    // Anular devuelve la mercadería al stock.
    const tocados: Producto[] = []
    const movimientos: Movimiento[] = []
    // Además del stock agregado, hay que devolver la cantidad al lote
    // del que salió —si no, el kardex por lote queda permanentemente
    // por debajo del stock real, y con el tiempo la próxima venta se
    // queda sin lotes de dónde descontar aunque el producto "tenga
    // stock". Si la línea cruzó más de un lote solo queda registrado
    // el primero (`loteId`); ahí se topa la reposición en
    // `cantidadInicial` para no inflar ese lote por sobre lo que
    // alguna vez tuvo —mejor quedarse corto en el lote que inventar
    // cantidad que nunca existió ahí.
    const lotesTocados: Lote[] = []
    for (const [i, l] of venta.lineas.entries()) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) continue
      const copia = tocados.find((x) => x.id === p.id) ?? { ...p }
      copia.stock += l.cantidad
      if (!tocados.some((x) => x.id === p.id)) tocados.push(copia)

      if (l.loteId) {
        const lote = lotesTocados.find((x) => x.id === l.loteId) ?? lotes.find((x) => x.id === l.loteId)
        if (lote) {
          const copiaLote = { ...lote, cantidad: Math.min(lote.cantidad + l.cantidad, lote.cantidadInicial) }
          const i = lotesTocados.findIndex((x) => x.id === lote.id)
          if (i >= 0) lotesTocados[i] = copiaLote
          else lotesTocados.push(copiaLote)
        }
      }

      movimientos.push({
        // A propósito NO es un id al azar: se arma siempre igual a
        // partir de la venta y la línea. Así, si esta anulación se
        // reintenta después de fallar a medio camino, la base reconoce
        // el movimiento como ya aplicado (ver la guardia de
        // idempotencia en nb_aplicar_stock) y no devuelve la
        // mercadería al stock por segunda vez.
        id: `m_anul_${ventaId}_${i}`,
        productoId: p.id,
        tipo: 'ajuste',
        cantidad: l.cantidad,
        stockResultante: copia.stock,
        loteId: l.loteId,
        referenciaId: ventaId,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        observaciones: `Anulación de ${venta.folio}`,
        fecha: ahora(),
      })
    }

    const actualizada: Venta = { ...venta, estado: 'anulada' }
    const previo = respaldar(get, 'ventas', 'productos', 'lotes', 'movimientos')
    set((s) => ({
      ventas: s.ventas.map((v) => (v.id === ventaId ? actualizada : v)),
      productos: s.productos.map((p) => tocados.find((x) => x.id === p.id) ?? p),
      lotes: s.lotes.map((l) => lotesTocados.find((x) => x.id === l.id) ?? l),
      movimientos: [...movimientos, ...s.movimientos],
    }))

    // Las tres escrituras iban juntas en un Promise.all, y eso dejaba
    // la base a medio camino cuando una fallaba. El caso comprobado en
    // pruebas: falla la escritura de la venta y la mercadería igual
    // vuelve al stock. Quedaban unidades fantasma en el sistema que no
    // existen en la cámara, y la venta seguía figurando como pendiente
    // de entrega. Recargar no lo arreglaba: estaba así en la base.
    //
    // Ahora van en orden y la pantalla vuelve atrás si algo falla. Con
    // los ids de movimiento fijos de más arriba, reintentar la
    // anulación es seguro: el stock ya devuelto no se vuelve a sumar y
    // la venta se termina de marcar.
    try {
      // Devolver la mercadería al stock también es un delta: se suma
      // sobre lo que haya en la base ahora.
      const resultado = await adaptador.aplicarStock(movimientos)
      await Promise.all([
        adaptador.guardar('ventas', [actualizada]),
        adaptador.guardar('lotes', lotesTocados),
      ])
      set(conStockDeLaBase(resultado))
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'eliminar', 'venta', `Se anuló ${venta.folio}`, ventaId)
  },

  /* --------------------------- Stock --------------------------- */

  async ingresarStock(entrada, autor) {
    const { productos, adaptador, lotes } = get()
    const p = productos.find((x) => x.id === entrada.productoId)
    if (!p) throw new Error('El producto no existe.')
    if (entrada.cantidad <= 0) throw new Error('La cantidad debe ser mayor que cero.')

    const actualizado: Producto = {
      ...p,
      stock: p.stock + entrada.cantidad,
      // El último costo pagado manda: es el que refleja el margen real.
      precioCosto: entrada.costo > 0 ? entrada.costo : p.precioCosto,
    }

    const lote: Lote = {
      id: uid('l'),
      productoId: p.id,
      codigo:
        entrada.codigoLote?.trim() ||
        `L${new Date().getFullYear()}-${String(lotes.length + 1).padStart(3, '0')}`,
      cantidad: entrada.cantidad,
      cantidadInicial: entrada.cantidad,
      vencimiento: entrada.vencimiento || undefined,
      costo: entrada.costo || p.precioCosto,
      proveedor: entrada.proveedor,
      recibidoPor: entrada.recibidoPor,
      ingresado: entrada.fecha ? new Date(entrada.fecha).toISOString() : ahora(),
    }

    const movimiento: Movimiento = {
      id: uid('m'),
      productoId: p.id,
      tipo: 'ingreso',
      cantidad: entrada.cantidad,
      stockResultante: actualizado.stock,
      loteId: lote.id,
      proveedor: entrada.proveedor,
      costo: entrada.costo,
      recibidoPor: entrada.recibidoPor,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: entrada.observaciones,
      fecha: lote.ingresado,
    }

    const previo = respaldar(get, 'productos', 'lotes', 'movimientos')
    set((s) => ({
      productos: s.productos.map((x) => (x.id === p.id ? actualizado : x)),
      lotes: [lote, ...s.lotes],
      movimientos: [movimiento, ...s.movimientos],
    }))

    // El lote y la ficha van ANTES que el stock, a propósito. Si esto
    // se cae a la mitad, es mucho menos malo que quede un lote
    // registrado sin haber sumado el stock (se ve, y se corrige con un
    // ajuste) a que el stock suba sin lote que lo respalde: en ese caso
    // un reintento lo sumaría por segunda vez y el sistema quedaría con
    // el doble de mercadería de la que llegó en el camión.
    try {
      await Promise.all([
        // De la ficha solo viaja el precio de costo: el stock lo sube el
        // movimiento de más abajo, sumando sobre lo que hay en la base.
        adaptador.guardar('productos', [actualizado]),
        adaptador.guardar('lotes', [lote]),
      ])
      const resultado = await adaptador.aplicarStock([movimiento])
      set(conStockDeLaBase(resultado))
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'ingreso_stock',
      'producto',
      `+${entrada.cantidad} de ${p.nombre}`,
      p.id,
    )
  },

  async registrarOrdenIngreso(entrada, autor) {
    const { productos, adaptador, lotes } = get()
    if (!entrada.lineas.length) throw new Error('Agrega al menos un producto.')

    const productosTocados: Producto[] = []
    const lotesNuevos: Lote[] = []
    const movimientos: Movimiento[] = []
    const fecha = entrada.fecha ? new Date(entrada.fecha).toISOString() : ahora()
    let siguienteNumeroLote = lotes.length + 1

    for (const l of entrada.lineas) {
      const p = productos.find((x) => x.id === l.productoId)
      if (!p) throw new Error('Uno de los productos ya no existe.')
      if (l.cantidad <= 0) throw new Error(`${p.nombre}: la cantidad debe ser mayor que cero.`)

      const actualizado: Producto = {
        ...p,
        stock: p.stock + l.cantidad,
        // El último costo pagado manda: es el que refleja el margen real.
        precioCosto: l.costo > 0 ? l.costo : p.precioCosto,
      }
      productosTocados.push(actualizado)

      const lote: Lote = {
        id: uid('l'),
        productoId: p.id,
        codigo: l.codigoLote?.trim() || `L${new Date().getFullYear()}-${String(siguienteNumeroLote++).padStart(3, '0')}`,
        cantidad: l.cantidad,
        cantidadInicial: l.cantidad,
        vencimiento: l.vencimiento || undefined,
        costo: l.costo || p.precioCosto,
        proveedor: entrada.proveedor,
        recibidoPor: entrada.recibidoPor,
        ingresado: fecha,
      }
      lotesNuevos.push(lote)

      movimientos.push({
        id: uid('m'),
        productoId: p.id,
        tipo: 'ingreso',
        cantidad: l.cantidad,
        stockResultante: actualizado.stock,
        loteId: lote.id,
        proveedor: entrada.proveedor,
        costo: l.costo,
        recibidoPor: entrada.recibidoPor,
        usuarioId: autor.id,
        usuarioNombre: autor.nombre,
        observaciones: entrada.observaciones,
        fecha,
      })
    }

    const previo = respaldar(get, 'productos', 'lotes', 'movimientos')
    set((s) => ({
      productos: s.productos.map((p) => productosTocados.find((x) => x.id === p.id) ?? p),
      lotes: [...lotesNuevos, ...s.lotes],
      movimientos: [...movimientos, ...s.movimientos],
    }))

    // Mismo orden que en un ingreso suelto: primero los lotes y las
    // fichas, el stock al final. Ver el comentario en ingresarStock.
    try {
      await Promise.all([
        // Igual que en un ingreso suelto: de la ficha solo se guarda el
        // precio de costo nuevo, nunca el stock.
        adaptador.guardar('productos', productosTocados),
        adaptador.guardar('lotes', lotesNuevos),
      ])
      const resultado = await adaptador.aplicarStock(movimientos)
      set(conStockDeLaBase(resultado))
    } catch (e) {
      set(previo)
      throw e
    }

    const totalUnidades = entrada.lineas.reduce((s, l) => s + l.cantidad, 0)
    await get().anotar(
      autor,
      'ingreso_stock',
      'producto',
      `Orden recibida: ${entrada.lineas.length} ${entrada.lineas.length === 1 ? 'producto' : 'productos'} · ${totalUnidades} unidades${entrada.proveedor ? ` · ${entrada.proveedor}` : ''}`,
    )
  },

  async ajustarStock(productoId, nuevoStock, motivo, autor) {
    const { productos, adaptador } = get()
    const p = productos.find((x) => x.id === productoId)
    if (!p) return
    const delta = nuevoStock - p.stock
    if (delta === 0) return
    const actualizado = { ...p, stock: nuevoStock }
    const movimiento: Movimiento = {
      id: uid('m'),
      productoId,
      tipo: delta < 0 ? 'merma' : 'ajuste',
      cantidad: delta,
      stockResultante: nuevoStock,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: motivo,
      fecha: ahora(),
    }
    const previo = respaldar(get, 'productos', 'movimientos')
    set((s) => ({
      productos: s.productos.map((x) => (x.id === productoId ? actualizado : x)),
      movimientos: [movimiento, ...s.movimientos],
    }))
    // Un ajuste es un conteo físico —"en la cámara hay 22"—, así que se
    // manda el número final y no la diferencia: la base calcula el
    // delta real contra lo que tiene guardado. Si el POS vendió 8
    // mientras la app tenía la pantalla abierta, el ajuste deja el
    // stock en 22 igual, y el movimiento queda con la diferencia
    // verdadera en vez de la que suponía el navegador.
    let resultado: ResultadoStock
    try {
      resultado = await adaptador.aplicarStock([{ ...movimiento, stockFinal: nuevoStock }])
    } catch (e) {
      set(previo)
      throw e
    }
    set(conStockDeLaBase(resultado))
    // La bitácora anota la diferencia que quedó registrada de verdad,
    // no la que se calculó contra la copia en memoria.
    const real = resultado.movimientos.find((m) => m.id === movimiento.id)?.cantidad ?? delta
    await get().anotar(autor, 'ajuste', 'producto', `${p.nombre}: ${real > 0 ? '+' : ''}${real} — ${motivo}`, productoId)
  },

  async registrarDevolucion(entrada, autor) {
    const { ventas, productos, devoluciones, lotes, adaptador } = get()
    const venta = ventas.find((v) => v.id === entrada.ventaId)
    const producto = productos.find((p) => p.id === entrada.productoId)
    if (!venta || !producto) throw new Error('No se encontró la venta o el producto.')
    const linea = venta.lineas.find((l) => l.productoId === entrada.productoId)
    if (!linea) throw new Error('Ese producto no está en la venta.')
    // No basta con comparar contra lo vendido en la línea: hay que
    // restarle lo que YA se devolvió antes de esa misma venta y
    // producto, si no, devolver la misma línea varias veces infla el
    // stock sin límite (nada más lo impedía).
    const yaDevuelto = devoluciones
      .filter((d) => d.ventaId === venta.id && d.productoId === producto.id)
      .reduce((s, d) => s + d.cantidad, 0)
    const disponibleParaDevolver = linea.cantidad - yaDevuelto
    if (entrada.cantidad > disponibleParaDevolver) {
      throw new Error(
        disponibleParaDevolver > 0
          ? `Solo quedan ${disponibleParaDevolver} unidades sin devolver de esta línea.`
          : 'Ya se devolvió todo lo vendido en esta línea.',
      )
    }

    const devolucion: Devolucion = {
      id: uid('d'),
      ventaId: venta.id,
      ventaFolio: venta.folio,
      productoId: producto.id,
      productoNombre: producto.nombre,
      cantidad: entrada.cantidad,
      monto: linea.precioUnitario * entrada.cantidad,
      motivo: entrada.motivo,
      reponeStock: entrada.reponeStock,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      fecha: ahora(),
    }

    const actualizado = entrada.reponeStock
      ? { ...producto, stock: producto.stock + entrada.cantidad }
      : producto

    // Igual que al anular una venta: si vuelve al stock, también tiene
    // que volver al lote del que salió (topado en su cantidad
    // original), para que el kardex por lote no se desalinee del stock
    // agregado con cada devolución.
    let loteActualizado: Lote | null = null
    if (entrada.reponeStock && linea.loteId) {
      const lote = lotes.find((x) => x.id === linea.loteId)
      if (lote) {
        loteActualizado = { ...lote, cantidad: Math.min(lote.cantidad + entrada.cantidad, lote.cantidadInicial) }
      }
    }

    const movimiento: Movimiento = {
      id: uid('m'),
      productoId: producto.id,
      tipo: entrada.reponeStock ? 'devolucion' : 'merma',
      cantidad: entrada.reponeStock ? entrada.cantidad : 0,
      stockResultante: actualizado.stock,
      loteId: linea.loteId,
      referenciaId: devolucion.id,
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      observaciones: `Devolución ${venta.folio}: ${entrada.motivo}`,
      fecha: devolucion.fecha,
    }

    const previo = respaldar(get, 'devoluciones', 'productos', 'lotes', 'movimientos')
    set((s) => ({
      devoluciones: [devolucion, ...s.devoluciones],
      productos: s.productos.map((p) => (p.id === producto.id ? actualizado : p)),
      lotes: loteActualizado ? s.lotes.map((l) => (l.id === loteActualizado!.id ? loteActualizado! : l)) : s.lotes,
      movimientos: [movimiento, ...s.movimientos],
    }))
    // La devolución se registra ANTES de mover el stock. Es el mismo
    // criterio que en los ingresos: la fila de la devolución es lo que
    // impide devolver dos veces la misma línea (el tope `yaDevuelto` de
    // más arriba la cuenta). Si el stock subiera primero y la fila no
    // llegara a guardarse, un reintento pasaría el tope y sumaría la
    // mercadería por segunda vez.
    try {
      await Promise.all([
        adaptador.guardar('devoluciones', [devolucion]),
        ...(loteActualizado ? [adaptador.guardar('lotes', [loteActualizado])] : []),
      ])
      // Si repone stock suma la cantidad; si no, el movimiento va con
      // cero y solo deja constancia de la merma.
      const resultado = await adaptador.aplicarStock([movimiento])
      set(conStockDeLaBase(resultado))
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'devolucion',
      'venta',
      `${entrada.cantidad} × ${producto.nombre} de ${venta.folio}`,
      venta.id,
    )
  },

  /* ------------------------ Mantenedores ------------------------ */

  async guardarProducto(producto, autor) {
    const anterior = get().productos.find((p) => p.id === producto.id)
    const existe = Boolean(anterior)
    const previo = respaldar(get, 'productos', 'movimientos')
    set((s) => ({
      productos: existe
        ? s.productos.map((p) => (p.id === producto.id ? producto : p))
        : [producto, ...s.productos],
    }))

    // Si el stock cambió a mano desde el formulario (no desde "Ajustar
    // stock"), igual queda un movimiento — si no, el historial mentiría
    // sobre de dónde salió la diferencia. Un producto nuevo nace con
    // stock 0 en la base, así que su "stock inicial" también entra como
    // movimiento en vez de aparecer de la nada.
    const delta = anterior ? producto.stock - anterior.stock : producto.stock
    const movimiento: Movimiento | null =
      delta !== 0
        ? {
            id: uid('m'),
            productoId: producto.id,
            tipo: delta < 0 ? 'merma' : 'ajuste',
            cantidad: delta,
            stockResultante: producto.stock,
            usuarioId: autor.id,
            usuarioNombre: autor.nombre,
            observaciones: anterior
              ? 'Editado desde la ficha del producto.'
              : 'Stock inicial del producto.',
            fecha: ahora(),
          }
        : null
    if (movimiento) set((s) => ({ movimientos: [movimiento, ...s.movimientos] }))

    // Primero la ficha y después el stock, en ese orden: el movimiento
    // apunta al producto con una llave foránea, así que la fila tiene
    // que existir antes. El upsert de la ficha no toca stock ni
    // reservado (ver supabaseAdapter), y el campo Stock del formulario
    // viaja como conteo —"ahora hay N"— para que la base saque sola la
    // diferencia contra lo que tenga guardado.
    try {
      await get().adaptador.guardar('productos', [producto])
      if (movimiento) {
        const resultado = await get().adaptador.aplicarStock([
          { ...movimiento, stockFinal: producto.stock },
        ])
        set(conStockDeLaBase(resultado))
      }
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      existe ? 'editar' : 'crear',
      'producto',
      producto.nombre,
      producto.id,
    )
  },

  async eliminarProducto(id, autor) {
    const p = get().productos.find((x) => x.id === id)
    const previo = respaldar(get, 'productos')
    set((s) => ({ productos: s.productos.filter((x) => x.id !== id) }))
    try {
      await get().adaptador.eliminar('productos', [id])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'eliminar', 'producto', p?.nombre ?? id, id)
  },

  async guardarCliente(cliente, autor) {
    const existe = get().clientes.some((c) => c.id === cliente.id)
    const previo = respaldar(get, 'clientes')
    set((s) => ({
      clientes: existe
        ? s.clientes.map((c) => (c.id === cliente.id ? cliente : c))
        : [cliente, ...s.clientes],
    }))
    try {
      await get().adaptador.guardar('clientes', [cliente])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, existe ? 'editar' : 'crear', 'cliente', cliente.nombre, cliente.id)
  },

  async eliminarCliente(id, autor) {
    // Si ya tiene ventas, borrarlo de verdad las deja apuntando a un
    // cliente que ya no existe —las cifras agregadas (Estadísticas,
    // comisiones) siguen bien porque cada venta guarda su propio
    // "clienteNombre", pero su ficha (RUT, dirección, contacto)
    // desaparece para siempre y no hay forma de volver a ella desde
    // esa venta. Mejor no dejar borrar en ese caso.
    const tieneVentas = get().ventas.some((v) => v.clienteId === id)
    if (tieneVentas) {
      throw new Error(
        'Este cliente ya tiene ventas registradas —no se puede eliminar sin perder esa ficha para siempre. Si ya no compra, puedes dejarlo con una anotación en vez de borrarlo.',
      )
    }
    const c = get().clientes.find((x) => x.id === id)
    const previo = respaldar(get, 'clientes')
    set((s) => ({ clientes: s.clientes.filter((x) => x.id !== id) }))
    try {
      await get().adaptador.eliminar('clientes', [id])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'eliminar', 'cliente', c?.nombre ?? id, id)
  },

  async guardarUsuario(usuario, autor) {
    const existe = get().usuarios.some((u) => u.id === usuario.id)
    const previo = respaldar(get, 'usuarios')
    set((s) => ({
      usuarios: existe
        ? s.usuarios.map((u) => (u.id === usuario.id ? usuario : u))
        : [...s.usuarios, usuario],
    }))
    // Acá el respaldo importa más que en otros lados: por esta función
    // pasa la clave de administrador. Si la escritura falla y la
    // pantalla se queda mostrando la clave como puesta, el
    // administrador cierra tranquilo creyendo que su cuenta quedó
    // protegida, y no lo está.
    try {
      await get().adaptador.guardar('usuarios', [usuario])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, existe ? 'editar' : 'crear', 'usuario', usuario.nombre, usuario.id)
  },

  async eliminarUsuario(id, autor) {
    const u = get().usuarios.find((x) => x.id === id)
    const previo = respaldar(get, 'usuarios')
    set((s) => ({ usuarios: s.usuarios.filter((x) => x.id !== id) }))
    try {
      await get().adaptador.eliminar('usuarios', [id])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'eliminar', 'usuario', u?.nombre ?? id, id)
  },

  async guardarAjustes(ajustes, autor) {
    const previo = respaldar(get, 'ajustes')
    set({ ajustes })
    try {
      await get().adaptador.guardarAjustes(ajustes)
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'editar', 'ajustes', 'Se actualizó la configuración')
  },

  /* --------------------------- Gastos ---------------------------- */

  async guardarGasto(gasto, autor) {
    const existe = get().gastos.some((g) => g.id === gasto.id)
    const previo = respaldar(get, 'gastos')
    set((s) => ({
      gastos: existe ? s.gastos.map((g) => (g.id === gasto.id ? gasto : g)) : [gasto, ...s.gastos],
    }))
    try {
      await get().adaptador.guardar('gastos', [gasto])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      existe ? 'editar' : 'crear',
      'gasto',
      `${gasto.categoria} · ${gasto.descripcion}`,
      gasto.id,
    )
  },

  async eliminarGasto(id, autor) {
    const g = get().gastos.find((x) => x.id === id)
    const previo = respaldar(get, 'gastos')
    set((s) => ({ gastos: s.gastos.filter((x) => x.id !== id) }))
    try {
      await get().adaptador.eliminar('gastos', [id])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(autor, 'eliminar', 'gasto', g?.descripcion ?? id, id)
  },

  /* --------------------------- Asistencia -------------------------- */

  async marcarEntrada(sucursalId, trabajador, autor) {
    const yaAbierta = get().asistencia.some(
      (a) => a.sucursalId === sucursalId && a.usuarioId === trabajador.id && !a.salida,
    )
    if (yaAbierta) return
    const registro: Asistencia = {
      id: uid('as'),
      sucursalId,
      usuarioId: trabajador.id,
      usuarioNombre: trabajador.nombre,
      entrada: ahora(),
      salida: null,
    }
    const previo = respaldar(get, 'asistencia')
    set((s) => ({ asistencia: [registro, ...s.asistencia] }))
    // Si esto falla y la pantalla igual muestra la entrada marcada, el
    // trabajador se va tranquilo y su turno no queda registrado en
    // ninguna parte: ese día se le paga mal.
    try {
      await get().adaptador.guardar('asistencia', [registro])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'crear',
      'asistencia',
      `${trabajador.nombre} marcó entrada`,
      registro.id,
    )
  },

  async marcarSalida(sucursalId, usuarioId, autor) {
    const abierta = get()
      .asistencia.filter((a) => a.sucursalId === sucursalId && a.usuarioId === usuarioId && !a.salida)
      .sort((a, b) => b.entrada.localeCompare(a.entrada))[0]
    if (!abierta) return
    const cerrada: Asistencia = { ...abierta, salida: ahora() }
    const previo = respaldar(get, 'asistencia')
    set((s) => ({ asistencia: s.asistencia.map((a) => (a.id === cerrada.id ? cerrada : a)) }))
    try {
      await get().adaptador.guardar('asistencia', [cerrada])
    } catch (e) {
      set(previo)
      throw e
    }
    await get().anotar(
      autor,
      'editar',
      'asistencia',
      `${cerrada.usuarioNombre} marcó salida`,
      cerrada.id,
    )
  },

  /* -------------------------- Bitácora -------------------------- */

  async anotar(autor, accion, entidad, detalle, entidadId) {
    const registro: Bitacora = {
      id: uid('b'),
      usuarioId: autor.id,
      usuarioNombre: autor.nombre,
      accion,
      entidad,
      entidadId,
      detalle,
      fecha: ahora(),
    }
    set((s) => ({ bitacora: [registro, ...s.bitacora].slice(0, 5000) }))
    // La bitácora NUNCA puede voltear la operación que está anotando.
    //
    // Todas las funciones de este store terminan llamando acá, y hasta
    // ahora un fallo de esta escritura se propagaba hacia arriba como
    // si hubiera fallado la operación entera. El caso peor, reproducido
    // en pruebas: la venta quedaba guardada en la base y con el stock
    // ya descontado, pero el vendedor veía "no se pudo registrar la
    // venta". Como en ese camino la pantalla no se limpia ni navega, lo
    // natural es confirmar de nuevo —y quedaban dos ventas y el stock
    // descontado dos veces, sin que nada lo advirtiera—.
    //
    // Anotar quién hizo qué es importante, pero es un registro de
    // auditoría: vale menos que la venta, la entrega o el ingreso que
    // acompaña. Si no se puede guardar, se pierde la anotación y la
    // operación sigue en pie.
    try {
      await get().adaptador.guardar('bitacora', [registro])
    } catch (e) {
      // Se saca también de la memoria: dejarla en pantalla sería
      // mostrar un registro que en la base no existe y que desaparece
      // solo al recargar.
      set((s) => ({ bitacora: s.bitacora.filter((b) => b.id !== registro.id) }))
      console.error('No se pudo guardar en la bitácora:', e)
    }
  },

  /* -------------------------- Respaldo -------------------------- */

  exportarRespaldo() {
    const {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      asistencia,
      ajustes,
    } = get()
    return {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      asistencia,
      ajustes,
    }
  },

  async restaurarRespaldo(snapshot) {
    await get().adaptador.reemplazarTodo(snapshot)
    set({ ...ordenar(snapshot) })
  },

  async cargarDemo() {
    const semilla = construirSemilla()
    await get().adaptador.reemplazarTodo(semilla)
    set({ ...ordenar(semilla) })
  },

  async borrarTodo() {
    const vacio = snapshotVacio()
    // Se conservan los usuarios: sin ellos nadie podría volver a entrar.
    vacio.usuarios = get().usuarios
    await get().adaptador.reemplazarTodo(vacio)
    set({ ...vacio })
  },
}))

/* ------------------------- Selectores ------------------------- */

type TablaGuardable = Tabla

export const tablasDisponibles: TablaGuardable[] = [
  'usuarios',
  'productos',
  'lotes',
  'movimientos',
  'clientes',
  'ventas',
  'devoluciones',
  'bitacora',
]

/** Alertas que el sistema levanta solo: stock bajo, vencimientos,
 *  créditos por cobrar y clientes a los que ya toca volver a ofrecer.
 *  Se calculan al vuelo desde el estado.
 *
 *  El aviso de "seguimiento" es personal: si se pasa `contexto`, un
 *  vendedor solo ve los suyos (los clientes que él mismo atendió la
 *  última vez); un administrador los ve todos. Sin contexto (por
 *  compatibilidad) se muestran todos. */
export function calcularNotificaciones(
  s: Snapshot,
  contexto?: { usuarioId: string; esAdmin: boolean },
): Notificacion[] {
  const avisos: Notificacion[] = []
  const umbralGlobal = s.ajustes.stockMinimoGlobal

  for (const p of s.productos) {
    if (!p.activo) continue
    const estado = estadoStock(p, umbralGlobal)
    if (estado === 'agotado') {
      avisos.push({
        id: `n_ago_${p.id}`,
        tipo: 'agotado',
        titulo: `${p.nombre} sin stock`,
        detalle: 'Reponer para poder seguir vendiéndolo.',
        enlace: `/inventario/${p.id}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    } else if (estado === 'poco') {
      avisos.push({
        id: `n_baj_${p.id}`,
        tipo: 'stock_bajo',
        titulo: `${p.nombre} con poco stock`,
        detalle: `Quedan ${p.stock - p.reservado} unidades.`,
        enlace: `/inventario/${p.id}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const l of s.lotes) {
    if (!l.vencimiento || l.cantidad <= 0) continue
    const dias = diasHasta(l.vencimiento)
    if (dias <= s.ajustes.diasAvisoVencimiento) {
      const p = s.productos.find((x) => x.id === l.productoId)
      avisos.push({
        id: `n_ven_${l.id}`,
        tipo: 'vencimiento',
        titulo:
          dias < 0
            ? `${p?.nombre ?? 'Lote'} vencido`
            : `${p?.nombre ?? 'Lote'} vence en ${dias} ${dias === 1 ? 'día' : 'días'}`,
        detalle: `Lote ${l.codigo} · ${l.cantidad} unidades.`,
        enlace: `/inventario/${l.productoId}`,
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const v of s.ventas) {
    if (v.metodoPago !== 'credito' || v.pagado || v.estado === 'anulada') continue
    const dias = diasHasta(v.vencimientoPago)
    if (dias <= 3) {
      avisos.push({
        id: `n_cob_${v.id}`,
        tipo: 'cobranza',
        titulo: dias < 0 ? `${v.clienteNombre} con pago vencido` : `${v.clienteNombre} vence pronto`,
        detalle: `${v.folio} · ${v.total.toLocaleString('es-CL')} pesos.`,
        enlace: '/cobranza',
        fecha: new Date().toISOString(),
        leida: false,
      })
    }
  }

  for (const sg of seguimientosPendientes(s.ventas, s.clientes)) {
    if (contexto && !contexto.esAdmin && sg.usuarioId !== contexto.usuarioId) continue
    avisos.push({
      id: `n_seg_${sg.clienteId}`,
      tipo: 'seguimiento',
      titulo: `${sg.clienteNombre}: ya toca volver a ofrecer`,
      detalle: `Pide cada ${sg.frecuenciaPedidoDias} días · van ${sg.diasDesde} sin comprar.`,
      enlace: `/clientes/${sg.clienteId}`,
      fecha: new Date().toISOString(),
      leida: false,
    })
  }

  return avisos
}
